import * as React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { IAdminProfile, IAdminProfileDetails } from '../models/IAdminProfile';
import { IAdminProfileTemplate } from '../models/IAdminProfileTemplate';
import { AdminProfilesService } from '../services/AdminProfilesService';
import { showCreateAdminProfileDialog } from '../services/CreateAdminProfileDialogAction';
import { showDeleteAdminProfileDialog } from '../services/DeleteAdminProfileDialogAction';
import { showEditAdminProfileDialog } from '../services/EditAdminProfileDialogAction';
import { AdminProfilesSpace } from './AdminProfilesSpace';
import { getAdminProfileServerUsers } from './tabs/AdminProfileUsersTabProvider';

const ServerAdminProfilesSpaceInfo: SpaceInfo = {
    spaceType: 'serveradminprofiles',
    subspaceName: undefined,
    item: SpaceItemForNonEntitySpace
};

export type AdminProfileSubspace = 'permissions' | 'sites' | 'users';

export interface ServerAdminProfilesSpaceProviderProps {
  profileId: string | null;
  subspace: string;
}

export interface ServerAdminProfilesSpaceProviderState {
  profiles: IAdminProfile[];
  isLoadingProfiles: boolean;
  selectedProfileDetails: IAdminProfileDetails | null;
  isLoadingDetails: boolean;
}

export class ServerAdminProfilesSpaceProvider extends React.PureComponent<ServerAdminProfilesSpaceProviderProps, ServerAdminProfilesSpaceProviderState> {

  public constructor(props: ServerAdminProfilesSpaceProviderProps) {
    super(props);
    this.state = {
      profiles: [],
      isLoadingProfiles: true,
      selectedProfileDetails: null,
      isLoadingDetails: false,
    };
  }

  public componentDidMount() {
    this.getProfiles();
    if (this.props.profileId) {
      this.loadProfileDetails(this.props.profileId);
    }
  }

  public componentDidUpdate(prevProps: ServerAdminProfilesSpaceProviderProps) {
    if (prevProps.profileId !== this.props.profileId) {
      this.setState({ selectedProfileDetails: null });
      if (this.props.profileId) {
        this.loadProfileDetails(this.props.profileId);
      }
    }
  }

  public render() {
      return (<ConnectedSpaceInfoProvider spaceInfo={ServerAdminProfilesSpaceInfo}>
        <AdminProfilesSpace
          profiles={this.state.profiles}
          isLoadingProfiles={this.state.isLoadingProfiles}
          selectedProfileId={this.props.profileId}
          selectedProfileDetails={this.state.selectedProfileDetails}
          isLoadingDetails={this.state.isLoadingDetails}
          activeSubspace={this.activeSubspace()}
          onAddProfile={this.handleAddProfile}
          onEditProfile={this.handleEditProfile}
          onDeleteProfile={this.handleDeleteProfile}
          onSelectProfile={this.handleSelectProfile}
          onProfileSitesUpdated={this.handleProfileSitesUpdated}
          urlForSubspace={this.urlForSubspace}
        />
      </ConnectedSpaceInfoProvider>);
  }

  private activeSubspace(): AdminProfileSubspace {
    const valid: AdminProfileSubspace[] = ['permissions', 'sites', 'users'];
    return (valid as string[]).includes(this.props.subspace)
      ? this.props.subspace as AdminProfileSubspace
      : 'permissions';
  }

  private loadProfileDetails = async (luid: string): Promise<void> => {
    this.setState({ isLoadingDetails: true });
    try {
      const details = await AdminProfilesService.getAdminProfileDetails(luid);
      const adminProfileServerUsersCount = getAdminProfileServerUsers(details.users ?? []).length;
      this.setState(prevState => ({
        selectedProfileDetails: details,
        isLoadingDetails: false,
        profiles: prevState.profiles.some(p => p.luid === luid)
          ? prevState.profiles.map(p =>
              p.luid === luid ? { ...p, userCount: adminProfileServerUsersCount } : p
            )
          : prevState.profiles,
      }));
    } catch {
      this.setState({ isLoadingDetails: false });
    }
  };

  private handleSelectProfile = (profile: IAdminProfile): void => {
    Location.instance.setUrl(ServerUrls.getInstance().adminProfilePermissionsUrl(profile.luid));
  };

  private handleProfileSitesUpdated = (): void => {
    if (this.props.profileId) {
      this.getProfiles().then(() => this.loadProfileDetails(this.props.profileId));
    }
  };

  private urlForSubspace = (subspace?: AdminProfileSubspace): string => {
    if (!this.props.profileId) {
      return ServerUrls.getInstance().adminProfilesUrl();
    }
    return ServerUrls.getInstance().adminProfileUrl(
      this.props.profileId, subspace || 'permissions'
    );
  };

  private getProfiles = async (): Promise<void> => {
    this.setState({ isLoadingProfiles: true });
    try {
      const profiles = await AdminProfilesService.getAdminProfilesForServer();
      this.setState({ profiles, isLoadingProfiles: false });
    } catch (error) {
      this.setState({ isLoadingProfiles: false });
    }
  };

  private handleAddProfile = async (): Promise<void> => {
    let template: IAdminProfileTemplate | null = null;
    try {
      template = await AdminProfilesService.getAdminProfileTemplate();
    } catch (error) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.createAdminProfileLoadTemplateFailed() });
      return;
    }

    const existingNames = this.state.profiles.map(p => p.name);
    showCreateAdminProfileDialog(template, existingNames, this.handleCreateSuccess);
  };

  private handleEditProfile = async (profile: IAdminProfile): Promise<void> => {
    let template: IAdminProfileTemplate | null = null;
    let details = null;
    try {
      [template, details] = await Promise.all([
        AdminProfilesService.getAdminProfileTemplate(),
        AdminProfilesService.getAdminProfileDetails(profile.luid),
      ]);
    } catch (error) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.createAdminProfileLoadTemplateFailed() });
      return;
    }

    const existingNames = this.state.profiles
      .filter(p => p.luid !== profile.luid)
      .map(p => p.name);
    showEditAdminProfileDialog(template, details, existingNames, this.handleEditSuccess);
  };

  private handleEditSuccess = async (luid: string): Promise<void> => {
    await this.getProfiles();
    await this.loadProfileDetails(luid);
    Location.instance.setUrl(ServerUrls.getInstance().adminProfilePermissionsUrl(luid));
  };

  private handleDeleteProfile = (profile: IAdminProfile): void => {
    showDeleteAdminProfileDialog(profile, this.handleDeleteSuccess);
  };

  private handleDeleteSuccess = async (): Promise<void> => {
    await this.getProfiles();
    Location.instance.setUrl(ServerUrls.getInstance().adminProfilesUrl());
  };

  private handleCreateSuccess = async (luid: string): Promise<void> => {
    await this.getProfiles();
    Location.instance.setUrl(ServerUrls.getInstance().adminProfilePermissionsUrl(luid));
  };
}
